import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Rocket, 
  Target, 
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  CircleCheck,
  RefreshCw,
  XCircle,
  AlertTriangle,
  TrendingDown,
  Gauge
} from "lucide-react";
import { cn } from "@/lib/utils";

interface WeekData {
  focus: string;
  tasks: string[];
  kpis?: string[];
  decision_point?: string;
}

interface DecisionThresholds {
  go: string;
  iterate: string;
  pivot: string;
  primary_kpi?: string;
  north_star_metric?: string;
  supporting_kpis?: string[];
}

interface PivotTrigger {
  week: number;
  metric: string;
  threshold: string;
  action: string;
}

interface FailureAnalysis {
  failure_indicators?: string[];
  pivot_triggers?: string[];
  abort_criteria?: string;
}

interface ActionPlanSectionProps {
  data: {
    week_1?: WeekData;
    week_2?: WeekData;
    week_3?: WeekData;
    week_4?: WeekData;
    hypotheses_to_test?: string[];
    data_to_collect?: string[];
    decision_thresholds?: DecisionThresholds;
    pivot_triggers?: PivotTrigger[];
    // Legacy structure fallback
    horizon_90_days?: Array<{ headline: string; description?: string }>;
    kpis?: string[];
    risks?: Array<{ risk: string; mitigation: string }>;
    executive_note?: string;
  };
  failureAnalysis?: FailureAnalysis;
  sessionId?: string;
  workspaceId?: string;
}

import { ImportHypothesesButton } from "./ImportHypothesesButton";
import { ImportFailureIndicatorsButton } from "./ImportFailureIndicatorsButton";
import { ImportActionTasksButton } from "./ImportActionTasksButton";

export function ActionPlanSection({ data, failureAnalysis, sessionId, workspaceId }: ActionPlanSectionProps) {
  if (!data) return null;

  // Check if we have the new week-based structure
  const hasNewStructure = data.week_1 || data.week_2 || data.week_3 || data.week_4;

  // Build weeks array
  const weeks: Array<{ label: string; weekNum: number; data: WeekData }> = [];
  if (data.week_1) weeks.push({ label: "Woche 1", weekNum: 1, data: data.week_1 });
  if (data.week_2) weeks.push({ label: "Woche 2", weekNum: 2, data: data.week_2 });
  if (data.week_3) weeks.push({ label: "Woche 3", weekNum: 3, data: data.week_3 });
  if (data.week_4) weeks.push({ label: "Woche 4", weekNum: 4, data: data.week_4 });

  // Fallback to legacy structure
  if (!hasNewStructure && data.horizon_90_days) {
    const actions = data.horizon_90_days;
    const chunkSize = Math.ceil(actions.length / 4);
    for (let i = 0; i < 4; i++) {
      const chunk = actions.slice(i * chunkSize, (i + 1) * chunkSize);
      if (chunk.length > 0) {
        weeks.push({
          label: `Woche ${i + 1}`,
          weekNum: i + 1,
          data: {
            focus: chunk[0]?.headline || `Woche ${i + 1}`,
            tasks: chunk.map(item => item.headline),
            kpis: [],
          }
        });
      }
    }
  }

  const thresholds = data.decision_thresholds;
  const pivotTriggers = data.pivot_triggers || [];

  // Get pivot trigger for a specific week
  const getPivotTriggerForWeek = (weekNum: number) => {
    return pivotTriggers.find(t => t.week === weekNum);
  };

  return (
    <Card id="action-plan" className="scroll-mt-24">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary" />
              30-Tage-Aktionsplan
            </CardTitle>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px] w-fit">
                Sprint 1 von 2 — Validierung 60 Tage
              </Badge>
              <Badge variant="secondary" className="text-[10px] bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
                KI-Vorschlag — anpassen empfohlen
              </Badge>
            </div>
          </div>
          {sessionId && workspaceId && weeks.length > 0 && (
            <ImportActionTasksButton
              sessionId={sessionId}
              workspaceId={workspaceId}
              tasks={weeks.flatMap(w => 
                (w.data.tasks || []).map(task => ({
                  title: task,
                  description: `${w.label}: ${w.data.focus || ''}`.trim(),
                }))
              )}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6 system-derived-compact">
        {/* 4-Week Timeline - With visual timeline on left */}
        {weeks.length > 0 && (
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-primary/20 hidden md:block" />
            
            <div className="space-y-4">
              {weeks.map((week, index) => {
                const pivotTrigger = getPivotTriggerForWeek(week.weekNum);
                
                return (
                  <div 
                    key={index} 
                    className={cn(
                      "relative border rounded-lg p-5 md:ml-10",
                      index === 3 ? 'bg-primary/5 border-primary/30' : 'bg-card border'
                    )}
                  >
                    {/* Timeline Dot */}
                    <div className={cn(
                      "absolute -left-10 top-6 w-5 h-5 rounded-full border-2 hidden md:flex items-center justify-center",
                      index === 3 
                        ? "bg-primary border-primary text-primary-foreground" 
                        : "bg-background border-primary/50"
                    )}>
                      <span className="text-[10px] font-bold">{index + 1}</span>
                    </div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Badge variant={index === 3 ? "default" : "outline"} className="text-xs">
                          {week.label}
                        </Badge>
                        <span className="font-semibold text-sm break-words">{week.data.focus}</span>
                      </div>
                      {week.data.decision_point && (
                        <Badge variant="secondary" className="text-xs">
                          <Target className="h-3 w-3 mr-1" />
                          Entscheidungspunkt
                        </Badge>
                      )}
                    </div>
                    
                    {/* Tasks as checkboxes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Aufgaben</p>
                        <ul className="space-y-2 text-sm">
                          {week.data.tasks?.map((task, taskIndex) => (
                            <li key={taskIndex} className="flex items-start gap-2">
                              <input type="checkbox" disabled className="mt-1 h-3.5 w-3.5 rounded border-primary/50 accent-primary shrink-0" />
                              <span className="text-foreground/90">{task}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* KPIs */}
                      {week.data.kpis && week.data.kpis.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-2">Messbare Ergebnisse</p>
                          <ul className="space-y-2 text-sm">
                            {week.data.kpis.map((kpi, kpiIndex) => (
                              <li key={kpiIndex} className="flex items-start gap-2">
                                <Target className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                <span>{kpi}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {/* Decision Gate */}
                    {(() => {
                      const dp = week.data.decision_point?.trim() || '';
                      const trivialPatterns = [/^go\s*\/?\s*iterate\s*\/?\s*pivot$/i, /^entscheidung\s+woche/i];
                      const isTrivial = !dp || trivialPatterns.some(p => p.test(dp));
                      
                      return (
                        <div className="mt-4 pt-3 border-t border-primary/40 bg-primary/10 rounded-lg p-3 -mx-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Target className="h-4 w-4 text-primary" />
                            <Badge variant="outline" className="text-[10px] border-primary/30">Woche {week.weekNum} Gate</Badge>
                            <Badge className="bg-green-500/20 text-green-700 dark:text-green-400 text-[10px] border-0">Go</Badge>
                            <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400 text-[10px] border-0">Iterate</Badge>
                            <Badge className="bg-red-500/20 text-red-700 dark:text-red-400 text-[10px] border-0">Pivot</Badge>
                          </div>
                          {!isTrivial && (
                            <p className="text-xs text-muted-foreground mt-2">
                              {dp}
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    {/* Pivot Trigger for this week */}
                    {pivotTrigger && (
                      <div className="mt-4 pt-3 border-t border-red-500/20 bg-red-500/5 rounded-lg p-3 -mx-2">
                        <div className="flex items-start gap-2">
                          <Gauge className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                          <div className="text-xs">
                            <p className="font-semibold text-red-700 dark:text-red-400 mb-1">
                              Abbruch-/Anpassungskriterium
                            </p>
                            <p className="text-muted-foreground">
                              <strong>Metrik:</strong> {pivotTrigger.metric}
                            </p>
                            <p className="text-muted-foreground">
                              <strong>Schwelle:</strong> {pivotTrigger.threshold}
                            </p>
                            <p className="text-red-600 dark:text-red-400 mt-1">
                              → {pivotTrigger.action}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Go/Iterate/Pivot Decision Thresholds with North-Star Metric */}
        {thresholds && (
          <div className="bg-gradient-to-r from-green-500/10 via-amber-500/10 to-red-500/10 rounded-xl p-5 border">
            <div className="flex items-center gap-2 mb-4">
              <ArrowRight className="h-5 w-5 text-primary" />
              <h4 className="font-semibold">Entscheidungskriterien nach 30 Tagen</h4>
            </div>
            
            {/* North-Star Metric - Prominent */}
            {(thresholds.primary_kpi || thresholds.north_star_metric) && (
              <div className="bg-primary/15 border border-primary/30 rounded-lg p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Gauge className="h-6 w-6 text-primary" />
                  <span className="font-bold text-primary text-lg">North-Star-Metrik</span>
                </div>
                {thresholds.north_star_metric && (
                  <p className="text-xl font-bold mb-1">{thresholds.north_star_metric}</p>
                )}
                {thresholds.primary_kpi && (
                  <p className={`${thresholds.north_star_metric ? 'text-sm text-muted-foreground' : 'text-lg font-semibold'}`}>
                    {thresholds.primary_kpi}
                  </p>
                )}
                {/* Expected Value Levels */}
                <div className="mt-4 pt-3 border-t border-primary/20 grid grid-cols-3 gap-2">
                  <div className="text-center p-2 rounded bg-green-500/10">
                    <p className="text-[10px] uppercase tracking-wide text-green-700 dark:text-green-400 font-semibold">Stretch (Go)</p>
                    <p className="text-xs mt-1">{thresholds.go}</p>
                  </div>
                  <div className="text-center p-2 rounded bg-amber-500/10">
                    <p className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 font-semibold">Minimum Viable</p>
                    <p className="text-xs mt-1">{thresholds.iterate}</p>
                  </div>
                  <div className="text-center p-2 rounded bg-red-500/10">
                    <p className="text-[10px] uppercase tracking-wide text-red-700 dark:text-red-400 font-semibold">Below Threshold</p>
                    <p className="text-xs mt-1">{thresholds.pivot}</p>
                  </div>
                </div>
                {thresholds.supporting_kpis && thresholds.supporting_kpis.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-primary/20">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5">Unterstützende KPIs</p>
                    <ul className="space-y-1">
                      {thresholds.supporting_kpis.map((kpi, i) => (
                        <li key={i} className="text-sm flex items-start gap-1.5">
                          <Target className="h-3 w-3 text-primary shrink-0 mt-0.5" />
                          <span>{kpi}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* GO */}
              <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <CircleCheck className="h-5 w-5 text-green-600" />
                  <span className="font-bold text-green-700 dark:text-green-400">GO</span>
                </div>
                <p className="text-sm font-medium">{thresholds.go}</p>
              </div>

              {/* ITERATE */}
              <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="h-5 w-5 text-amber-600" />
                  <span className="font-bold text-amber-700 dark:text-amber-400">ITERATE</span>
                </div>
                <p className="text-sm font-medium">{thresholds.iterate}</p>
              </div>

              {/* PIVOT */}
              <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="font-bold text-red-700 dark:text-red-400">PIVOT</span>
                </div>
                <p className="text-sm font-medium">{thresholds.pivot}</p>
              </div>
            </div>
            
            {/* Unified thresholds note */}
            <p className="text-xs text-muted-foreground text-center mt-4 pt-3 border-t">
              Wochen-KPIs sind Frühindikatoren für die North-Star-Metrik. Abweichungen in frühen Wochen erfordern Kurskorrektur, nicht Abbruch.
            </p>
          </div>
        )}

        {/* Hypotheses to Test */}
        {data.hypotheses_to_test && data.hypotheses_to_test.length > 0 && (
          <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                <h4 className="font-semibold text-sm">Zu validierende Hypothesen</h4>
              </div>
              {sessionId && workspaceId && (
                <ImportHypothesesButton
                  sessionId={sessionId}
                  workspaceId={workspaceId}
                  hypotheses={data.hypotheses_to_test}
                />
              )}
            </div>
            <ul className="space-y-2 text-sm">
              {data.hypotheses_to_test.map((hyp, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold">{index + 1}.</span>
                  <span>{hyp}</span>
                </li>
              ))}
            </ul>
            {sessionId && workspaceId && (
              <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-amber-500/20">
                Füge Hypothesen zum Tracking hinzu, um sie später in deinem Workspace zu verfolgen.
              </p>
            )}
          </div>
        )}

        {/* Failure Analysis */}
        {failureAnalysis && (failureAnalysis.failure_indicators?.length || failureAnalysis.pivot_triggers?.length) && (
          <div className="bg-destructive/5 rounded-lg p-4 border border-destructive/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <h4 className="font-semibold text-sm">Warnsignale & Abbruchkriterien</h4>
              </div>
              {sessionId && workspaceId && failureAnalysis.failure_indicators && failureAnalysis.failure_indicators.length > 0 && (
                <ImportFailureIndicatorsButton
                  sessionId={sessionId}
                  workspaceId={workspaceId}
                  indicators={failureAnalysis.failure_indicators}
                />
              )}
            </div>
            
            {failureAnalysis.failure_indicators && failureAnalysis.failure_indicators.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-muted-foreground mb-2">Frühe Warnsignale</p>
                <ul className="space-y-2 text-sm">
                  {failureAnalysis.failure_indicators.map((indicator, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <TrendingDown className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                      <span>{indicator}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {failureAnalysis.pivot_triggers && failureAnalysis.pivot_triggers.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Pivot-Trigger</p>
                <ul className="space-y-2 text-sm">
                  {failureAnalysis.pivot_triggers.map((trigger, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                      <span>{trigger}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {failureAnalysis.abort_criteria && (
              <div className="mt-4 pt-3 border-t border-red-500/20">
                <p className="text-sm">
                  <strong className="text-red-600">Abbruch wenn:</strong> {failureAnalysis.abort_criteria}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
